<template>
    <div>
        <div>
            <p class="text-lg leading-6 font-medium text-gray-900">
                Add a user
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
              Each user will be asked to fill out additional information upon Login.
              Admin Users will receive an account activation email immediately.
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
              View users will receive an email upon completion of the Implementation Wizard.
            </p>
        </div>
        <banner-success v-model:isOpen="successBanner" :auto-hide="5000">
            <template #title>Successfully added User</template>
            <template #content>Added user {{lastUser}}</template>
        </banner-success>
        <banner-error v-model:isOpen="hasError">
            <template #title>Error Adding User</template>
            <template #content>
                <span   v-if="hasErrorFirstName" class="py-2">First Name is Required.</span>
                <span   v-if="hasErrorLastName" class="py-2">Last Name is Required.</span>
                <span   v-if="hasErrorEmail" class="py-2">Email is Required and must be a valid email.</span>
                <span   v-if="hasErrorEmailIsDeleted" class="py-2">
                    This email address has been previously used and removed from the system.
                    Please contact support to re-activate this user.
                </span>
                <span   v-if="hasErrorPermissionLevel" class="py-2">
                    Unable to create a user of that permission on this company.  Please contact a user with
                    sufficient permissions.
                </span>
                <span   v-if="hasErrorUserExists" class="py-2">
                    User Already exists on this site.  Please continue with the next user.
                </span>
            </template>
        </banner-error>
        <form>
            <div class="pt-2">
                <div class="">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
                        <div class="mt-1 sm:mt-0 sm:col-span-2">
                            <floating-input     id="first_name"
                                                v-model:value="firstName"
                                                label="First Name"
                            ></floating-input>
                        </div>
                    </div>
                    <div class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                        <div class="mt-1 sm:mt-0 sm:col-span-2">
                            <floating-input     id="last_name"
                                                v-model:value="lastName"
                                                label="Last Name"
                            ></floating-input>
                        </div>
                    </div>
                    <div class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                        <div class="mt-1 sm:mt-0 sm:col-span-2">
                            <floating-input     id="email"
                                                v-model:value="email"
                                                label="Email"
                            ></floating-input>
                        </div>
                    </div>

                    <div class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                        <label class="block text-sm sm:col-span-2 font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2">
                          <p>
                            Make this person an Admin?
                          </p>
                          <p class="font-normal text-gray-500">
                            Admin users can manage alarms, reports, points, users, and general configuration of the site.
                          </p>
                          <p class="font-normal text-gray-500">
                            View users can view points, run reports, and take action on alarms.
                          </p>
                        </label>
                      <div class="mt-1 sm:mt-0 sm:col-span-2">
                                      <span class="relative z-0 inline-flex shadow-sm rounded-md">
                                        <button
                                            type="button"
                                            @click="adminStatus = false"
                                            :class="!adminStatus ? 'bg-blue-200' : 'bg-white'"
                                            class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                          View User
                                        </button>
                                        <button
                                            type="button"
                                            @click="adminStatus = true"
                                            :class="adminStatus ? 'bg-blue-200' : 'bg-white'"
                                            class="-ml-px relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                          Admin User
                                        </button>
                                      </span>
                      </div>
                    </div>

                    <div class="mt-6 sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                        <div class="col-span-1"></div>
                        <a class="flex justify-center plain-link">
                            <button type="button"
                                    @click="postData"
                                    class="relative items-center px-4 py-2 border border-transparent text-sm text-center leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-400 focus:outline-none focus:ring-green-200 focus:border-green-400 active:bg-green-400">
                                Add User
                            </button>
                        </a>
                    </div>
                </div>
            </div>
        </form>
      <notification-modal v-model:is-open="showViewUserModal" :is-error-modal="false">
        <template #title-content>
          View User Added!
        </template>

        <p>Your new user was created successfully.</p>

        <p>
          As a reminder, this user is <b>not</b> an admin, therefore they won't receive an invite until
          all Implementation Wizard steps up to <i>Alarm Activation</i> are completed.
        </p>
      </notification-modal>
    </div>
</template>

<script>
    import axios from 'axios';
    import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
    import BannerError from "@/components/Banners/BannerError";
    import BannerSuccess from "@/components/Banners/BannerSuccess";
    import FloatingInput from "@/components/elements/FloatingInput";
    import _ from 'lodash'
    import NotificationModal from "@/components/elements/NotificationModal";


    export default {
        name: "userAdd",
        mixins: [SonicuCoreMixin],
        components: {FloatingInput, BannerError, BannerSuccess, NotificationModal},
        data() {
            return {
                firstName: '',
                lastName: '',
                email: '',
                adminStatus: false,
                lastUser: '',
                errorText: '',
                successBanner: false,
                hasCreatedViewUser: false,
                showViewUserModal: false,
                errors: {},
            }
        },
        methods: {
            postData(e) {
                e.preventDefault();
                this.successBanner = false
                this.errors = {}
                axios.post(this.company.buildCompanyApiInternalRoute('wizard/users'), {
                    'first_name': this.firstName,
                    'last_name': this.lastName,
                    'email': this.email,
                    'email_confirmation': this.email,
                    'admin_status': this.adminStatus,
                    'company_id': this.company.getCompanyId(),
                }).then((response) => {
                    this.$emit('user-added', response.data.creatingUser ? 'creating-user' : null);
                    if(this.adminStatus === false){
                        this.showModalOnFirstViewUserAdd();
                    }
                    this.lastUser = this.firstName + ' ' + this.lastName;
                    this.firstName = '';
                    this.lastName = '';
                    this.email = '';
                    this.adminStatus = false;
                    this.successBanner = true
                }).catch((error) => {
                    console.log(`Error adding User`)
                    console.log(error.response.data)
                    this.errorText = ! _.isNil(error.response.data)
                        ? error.response.data
                        : 'Please contact Support if the issue persists';
                    this.errors = error.response.data;
                }).then(() => {
                    this.$forceUpdate()
                })
            },
            showModalOnFirstViewUserAdd(){
                if(this.hasCreatedViewUser){
                    return true;
                }
                this.hasCreatedViewUser = true;
                this.showViewUserModal = true;
            },
            checkError(key) {
                return this.hasError && _.has(this.errors, key) && this.errors[key] === true
            },
        },
        computed: {
            hasError() {
                return ! _.isNil(this.errors) && ! _.isEmpty(this.errors)
            },
            hasErrorFirstName() {
                return this.checkError('first_name')
            },
            hasErrorLastName() {
                return this.checkError('last_name')
            },
            hasErrorEmail() {
                return this.checkError('email')
            },
            hasErrorEmailIsDeleted() {
                return this.checkError('email_deleted')
            },
            hasErrorPermissionLevel() {
                return this.checkError('permission_error')
            },
            hasErrorUserExists() {
                return this.checkError('user_exists')
            },
        },
    }
</script>

<style scoped>

</style>